// Values shared by the database, API, web app and mobile app.
// The database enums in packages/db are built from these arrays, so they can't drift apart.

export const ROLES = ['user', 'manager', 'admin'] as const;
export type Role = (typeof ROLES)[number];

/**
 * How a driver is identified when they plug in. 'public' is no longer used (pay to charge is
 * a switch beside RFID cards since October 2026); it stays only because the database enum has it.
 */
export const CHARGER_AUTH_METHODS = ['plug_and_charge', 'rfid', 'public'] as const;
export type ChargerAuthMethod = (typeof CHARGER_AUTH_METHODS)[number];
/** The ones a charger can be set to. */
export const CHARGER_AUTH_CHOICES = ['rfid', 'plug_and_charge'] as const;

/** Plugs a connector can have. */
export const PLUG_TYPES = ['Type 2', 'CCS2'] as const;
export type PlugType = (typeof PLUG_TYPES)[number];

/** Whether a charger is allowed to talk to the central system. */
export const CHARGER_REGISTRATION = ['pending', 'accepted', 'rejected'] as const;
export type ChargerRegistration = (typeof CHARGER_REGISTRATION)[number];

/** OCPP 1.6 ChargePointStatus values, plus 'Offline' for no heartbeat. */
export const CONNECTOR_STATUSES = [
  'Available',
  'Preparing',
  'Charging',
  'SuspendedEVSE',
  'SuspendedEV',
  'Finishing',
  'Reserved',
  'Unavailable',
  'Faulted',
  'Offline',
] as const;
export type ConnectorStatus = (typeof CONNECTOR_STATUSES)[number];

export const TRANSACTION_STATUSES = ['active', 'completed', 'aborted'] as const;
export type TransactionStatus = (typeof TRANSACTION_STATUSES)[number];

/**
 * Who pays for a session. Nobody chooses it: the OCPP service stamps it when the session starts.
 *  - public: a pay-to-charge guest who paid by card
 *  - organisation: a driver's card, or the charger's plug-and-charge driver
 *  - unbilled: nobody (started from the app without a card, or the card was refused)
 * A free charger is a $ 0.00 price; charging at home is the plug-and-charge driver.
 */
export const BILLING_TYPES = ['organisation', 'public', 'unbilled'] as const;
export type BillingType = (typeof BILLING_TYPES)[number];

/** Pay-to-charge limit chosen by the guest: energy (Wh), money (cents) or time (minutes). */
export const PUBLIC_LIMIT_TYPES = ['energy', 'money', 'time'] as const;
export type PublicLimitType = (typeof PUBLIC_LIMIT_TYPES)[number];

export const PUBLIC_SESSION_STATUSES = [
  'created', // payment hold not yet confirmed
  'authorised', // hold confirmed by Stripe webhook, waiting for the charger to start
  'charging',
  'stopping',
  'completed', // charger stopped, amount not yet captured
  'captured', // final amount taken, rest released
  'cancelled', // hold released, nothing charged
  'failed',
] as const;
export type PublicSessionStatus = (typeof PUBLIC_SESSION_STATUSES)[number];

/** OCPP Security Profiles from the 1.6 Security Whitepaper. */
export const OCPP_SECURITY_PROFILES = {
  /** No authentication. Only for legacy chargers during cutover. */
  NONE: 0,
  /** HTTP Basic auth, plain ws. Not used in production. */
  BASIC: 1,
  /** HTTP Basic auth over TLS (wss). The target for every charger. */
  BASIC_TLS: 2,
} as const;
export type OcppSecurityProfile = (typeof OCPP_SECURITY_PROFILES)[keyof typeof OCPP_SECURITY_PROFILES];

export const OCPP_COMMAND_STATUSES = ['pending', 'sent', 'accepted', 'rejected', 'error', 'timeout'] as const;
export type OcppCommandStatus = (typeof OCPP_COMMAND_STATUSES)[number];

export const OCPP_DIRECTIONS = ['in', 'out'] as const;
export type OcppDirection = (typeof OCPP_DIRECTIONS)[number];
